import React, { useEffect, useState } from 'react';
import { dbService, type AgentInfo, type Profile } from '../lib/database';
import { Users, AlertTriangle, Plus, Trash2 } from 'lucide-react';

interface AgentWithEmail extends AgentInfo {
  user_email: string;
}

export function AdminAgentsList() {
  const [allAgents, setAllAgents] = useState<AgentWithEmail[]>([]);
  const [globalAgents, setGlobalAgents] = useState<AgentInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newAgentId, setNewAgentId] = useState('');
  const [newAgentName, setNewAgentName] = useState('');
  const [adding, setAdding] = useState(false);

  const fetchAllAgents = async () => {
    try {
      const [profiles, agents] = await Promise.all([
        dbService.getUserProfiles(),
        dbService.getAllAgents(),
      ]);

      const allAgentsFlattened = profiles.reduce<AgentWithEmail[]>((acc, profile) => {
        if (!profile.agents_info) return acc;

        const agentsArray = Array.isArray(profile.agents_info)
          ? profile.agents_info
          : [];

        const agentsWithEmail = agentsArray.map((agent: AgentInfo) => ({
          ...agent,
          user_email: profile.email
        }));

        return [...acc, ...agentsWithEmail];
      }, []);

      const sortedAgents = allAgentsFlattened.sort((a, b) =>
        a.display_name.localeCompare(b.display_name)
      );

      setAllAgents(sortedAgents);
      setGlobalAgents(agents);
    } catch (err) {
      console.error('Error fetching agents:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch agents');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllAgents();
  }, []);

  const handleAddAgent = async () => {
    if (!newAgentId.trim() || !newAgentName.trim()) return;
    setAdding(true);
    try {
      await dbService.addAgent({ agent_id: newAgentId.trim(), display_name: newAgentName.trim() });
      await fetchAllAgents();
      setNewAgentId('');
      setNewAgentName('');
      setShowAddForm(false);
    } catch (err) {
      console.error('Error adding agent:', err);
    } finally {
      setAdding(false);
    }
  };

  const handleRemoveGlobalAgent = async (agentId: string) => {
    try {
      await dbService.removeAgent(agentId);
      await fetchAllAgents();
    } catch (err) {
      console.error('Error removing agent:', err);
    }
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse flex items-center justify-center">
          <div className="text-zinc-400">Loading agents...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8">
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 text-red-500 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5" />
          <span>{error}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Users className="w-6 h-6 text-emerald-400" />
          <h2 className="text-2xl font-bold text-white">All Agents</h2>
        </div>
        <div className="flex items-center gap-3">
          <div className="bg-zinc-800/50 px-4 py-2 rounded-lg border border-zinc-700/50">
            <span className="text-zinc-400">Total Agents: </span>
            <span className="text-emerald-400 font-semibold">{globalAgents.length}</span>
          </div>
          <button
            onClick={() => setShowAddForm(!showAddForm)}
            className="flex items-center gap-1.5 px-4 py-2 bg-emerald-500 text-black text-sm rounded-lg hover:bg-emerald-400 transition"
          >
            <Plus className="w-4 h-4" />
            Add Agent
          </button>
        </div>
      </div>

      {showAddForm && (
        <div className="mb-6 p-4 bg-zinc-800/50 rounded-lg border border-zinc-700/50">
          <h3 className="text-sm font-medium text-zinc-300 mb-3">New Agent</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
            <input
              type="text"
              value={newAgentId}
              onChange={e => setNewAgentId(e.target.value)}
              placeholder="Agent ID (from your AI platform)"
              className="px-3 py-2 bg-zinc-900 border border-zinc-700 rounded-lg text-white text-sm focus:outline-none focus:border-emerald-500"
            />
            <input
              type="text"
              value={newAgentName}
              onChange={e => setNewAgentName(e.target.value)}
              placeholder="Display Name"
              className="px-3 py-2 bg-zinc-900 border border-zinc-700 rounded-lg text-white text-sm focus:outline-none focus:border-emerald-500"
            />
          </div>
          <div className="flex gap-2 justify-end">
            <button
              onClick={() => { setShowAddForm(false); setNewAgentId(''); setNewAgentName(''); }}
              className="px-3 py-1.5 text-sm text-zinc-400 hover:text-white transition"
            >
              Cancel
            </button>
            <button
              onClick={handleAddAgent}
              disabled={!newAgentId.trim() || !newAgentName.trim() || adding}
              className="px-4 py-1.5 bg-emerald-500 text-black text-sm rounded-lg hover:bg-emerald-400 transition disabled:opacity-50"
            >
              {adding ? 'Adding...' : 'Add Agent'}
            </button>
          </div>
        </div>
      )}

      {globalAgents.length > 0 && (
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-white mb-4">Registered Agents</h3>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {globalAgents.map((agent) => {
              const assignedTo = allAgents
                .filter(a => a.agent_id === agent.agent_id)
                .map(a => a.user_email);

              return (
                <div
                  key={agent.agent_id}
                  className="bg-zinc-800/50 rounded-lg p-5 border border-zinc-700/50 hover:border-emerald-500/30 transition-all group"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-emerald-500/10 flex items-center justify-center group-hover:bg-emerald-500/20 transition-colors">
                        <Users className="w-5 h-5 text-emerald-400" />
                      </div>
                      <div>
                        <h4 className="text-base font-semibold text-white group-hover:text-emerald-400 transition-colors">
                          {agent.display_name}
                        </h4>
                        <p className="text-xs text-zinc-500">ID: {agent.agent_id}</p>
                      </div>
                    </div>
                    <button
                      onClick={() => handleRemoveGlobalAgent(agent.agent_id)}
                      className="opacity-0 group-hover:opacity-100 p-1.5 text-zinc-500 hover:text-red-400 transition-all"
                      title="Remove agent"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  {assignedTo.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-zinc-700/50">
                      <p className="text-xs text-zinc-500 mb-1">Assigned to:</p>
                      {assignedTo.map(email => (
                        <p key={email} className="text-xs text-zinc-400">{email}</p>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {allAgents.length > 0 && (
        <>
          <h3 className="text-lg font-semibold text-white mb-4">Agent Assignments</h3>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {allAgents.map((agent) => (
              <div
                key={`${agent.agent_id}-${agent.user_email}`}
                className="bg-zinc-800/50 rounded-lg p-5 border border-zinc-700/50 hover:border-emerald-500/30 transition-all group"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-emerald-500/10 flex items-center justify-center group-hover:bg-emerald-500/20 transition-colors">
                    <Users className="w-5 h-5 text-emerald-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="text-base font-semibold text-white group-hover:text-emerald-400 transition-colors truncate">
                      {agent.display_name}
                    </h4>
                    <p className="text-xs text-zinc-500 truncate">ID: {agent.agent_id}</p>
                    <p className="text-xs text-zinc-400 mt-1 truncate">Customer: {agent.user_email}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {allAgents.length === 0 && globalAgents.length === 0 && (
        <div className="text-center py-12 bg-zinc-800/30 rounded-lg border border-zinc-700/30">
          <Users className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
          <p className="text-zinc-400">No agents found</p>
          <p className="text-sm text-zinc-500 mt-2">
            Click "Add Agent" above to register your first AI agent
          </p>
        </div>
      )}
    </div>
  );
}
